<template lang="pug">
  section.b-stages(v-if="project && currUser")
    StageSortChangePopup(:stages="stages" ref="stageSortChangePopup")
    ._main(ref="main")
      FormBlock._block(v-if="stagesFromStore.filter(s => s.id).length")
        ._filters
          BaseInput(v-model="filters.name" iconName="search" label="Поиск")
          BaseCheckbox(v-model="filters.hideEmptyStages" label="Скрыть пустые этапы")
          BaseCheckbox(v-model="filters.hideWithoutNotifications" label="Только с уведомлениями")
      template(v-if="stages.length")
        template(v-for="stage in stages")
          StageTile._block(
            :key="stage.id"
            :stage="stage"
            :project="project"
            :tasks="tasksOfStage(stage)"
            ref="stages"
            :id="stage.id"
            @tasksSelected="onTasksSelected(stage, $event)"
          )
      EmptyList._block(v-else vertical) Нет разделов проекта
        ._empty-buttons
          Btn(@click="stageModel.addToList(project)" dark xl)
            Icon(name="plus")
            | Новый раздел
          Btn(v-if="canApplyTemplate" @click="applyTemplate" xl)
            Icon(name="copy")
            | Применить шаблон
      ._footer(v-if="!editingStage && access.canEditStages()")
        Teleport(to=".project-detail-button")
          Btn(@click="stageModel.addToList(project)" dark xl)
            Icon(name="plus")
            | Новый раздел
    BaseTwoWaySticky._aside(:padding="30" :topOffset="35")
      FormBlock(mobile)._nav
        ._aside-header
          ._title Содержание проекта
          BtnResort(:disabled="!stages.length" @click="resortStages")
          BtnExpand(:expanded="!areAllStagesFolded" @toggle="toggleFolding")
        StageListNav(:stages="stages" :selected="selectedStage" @select="selectStage")
    Teleport(to="#footer-bar")
      TaskViewFooterBar(:tasks="selectedTasks" :selectedTasks="selectedTasks" :hideToggleAll="true" @update="update")
</template>

<script>
import Access from '@/utils/access';
import stageModel from '@/model/stage';
import projectModel from '@/model/project';
import taskListModel from '@/model/taskList';
import modelCustomSettings from '@/model/userCustomSettings';
import Teleport from 'vue2-teleport';

import FormBlock from '@/components/FormBlock.vue';
import UserTile from '@/components/UserTile.vue';
import StageTile from '@/components/StageTile.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import BtnExpand from '@/components/ui/BtnExpand.vue';
import StageListNav from '@/components/stage/StageListNav.vue';
import BaseTwoWaySticky from '@/components/base/BaseTwoWaySticky.vue';
import BtnResort from '@/components/ui/BtnResort.vue';
import StageSortChangePopup from '@/components/stage/StageSortChangePopup.vue';
import BaseInput from '@/components/BaseInput.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import TaskViewFooterBar from '@/components/task/view/TaskViewFooterBar.vue';

export default {
  name: 'ProjectStagesPage',
  components: {
    TaskViewFooterBar,
    BaseCheckbox,
    BaseInput,
    StageSortChangePopup,
    BtnResort,
    BtnExpand,
    StageListNav,
    EmptyList,
    StageTile,
    UserTile,
    FormBlock,
    Teleport,
    BaseTwoWaySticky,
  },
  data() {
    return {
      selectedStage: null,
      selectedTasksByStage: {},
      filters: {
        name: '',
        hideEmptyStages: false,
        hideWithoutNotifications: false,
      },
    };
  },
  computed: {
    currUser() { return this.$store.state.auth.user; },
    access() { return new Access(this.project, this.currUser); },
    project() { return this.$store.state.project.project; },
    projectMembers() { return this.$store.getters['project/members']; },
    editingStage() { return this.$store.state.stage.editingItem; },
    stageModel() { return stageModel; },
    foldedStages() { return this.currUser.customSettings.foldedStages ?? []; },
    stageIds() { return this.stages.map(s => s.id); },
    areAllStagesFolded() { return !this.stageIds.find(id => !this.foldedStages.includes(id)); },
    notificationCountByStage() { return this.$store.getters['notification/countByStage']; },
    notificationCountByTask() { return this.$store.getters['notification/countByTask']; },
    tasks() { return this.$store.getters['taskList/byStages']; },
    stagesFromStore() { return this.$store.getters['stage/itemsHydrated'] ?? []; },
    // шаблон применяется только к пустому проекту — без разделов и задач
    canApplyTemplate() {
      return this.access.canEditStages() && !this.stagesFromStore.length && !this.project.numberOfTasks;
    },
    stages() {
      const tasksOfStages = this.stagesFromStore.reduce((acc, val) => ({ ...acc, [val.id]: this.tasksOfStage(val) }), {});
      return this.stagesFromStore
        // .map(s => ({ ...s, tasks: this.tasksOfStage(s) }))
        .filter(s => (!this.filters.hideEmptyStages || tasksOfStages[s.id].length)
          && (!this.filters.name || tasksOfStages[s.id].length)
          && (!this.filters.hideWithoutNotifications || this.notificationCountByStage[s.id]));
    },
    stageToScroll() {
      const scrollTo = +this.$route.hash.replace('#', '');
      const stagesExist = this.stages?.length;
      const tasksExist = Object.keys(this.tasks).length;
      const exists = stagesExist && tasksExist && scrollTo;

      return exists ? scrollTo : null;
    },
    selectedTasks() {
      return Object.values(this.selectedTasksByStage)
        .reduce((acc, val) => [...acc, ...val], [])
        .map(t => ({
          ...t,
          project: this.project,
          canBeUpdated: new Access({ ...t, project: this.project }, this.currUser).canUpdateTask(true),
        }));
    },
  },
  created() {
    const { projectId } = this.$route.params;
    this.$store.commit('taskList/set', []);

    taskListModel.fetch({ 'stage.project': projectId });
  },
  methods: {
    update() {
      console.log('update');
      this.resetCheck();
      const { projectId } = this.$route.params;
      taskListModel.fetch({ 'stage.project': projectId });
    },
    async applyTemplate() {
      if (!await projectModel.applyTemplate(this.project)) return;

      // проект перечитывается целиком: вместе с ним приходят разделы, описание
      // и счётчики задач; задачи — отдельным списком. Файлы и папки не трогаем —
      // страница файлов сама перезапрашивает их при каждом открытии
      const { projectId } = this.$route.params;
      this.$store.dispatch('project/fetchProject', { projectId, force: true });
      taskListModel.fetch({ 'stage.project': projectId });
    },
    tasksOfStage(stage) {
      const filterStr = this.filters.name.toLowerCase().replaceAll(' ', '');
      return (this.tasks[stage.id] ?? [])
        .filter(t => (!filterStr || t.name.toLowerCase().replaceAll(' ', '').includes(filterStr))
          && (!this.filters.hideWithoutNotifications || this.notificationCountByTask[t.id]));
    },
    selectStage(id) {
      const ref = this.$refs.stages.find(r => r.$attrs.id === id);
      const top = ref.$el.getBoundingClientRect().top + window.scrollY - 30;
      window.scrollTo({ top, behavior: 'smooth' });
    },
    toggleFolding(expand) {
      let foldedStages = this.foldedStages.filter(i => this.stageIds.indexOf(i) === -1);

      if (!expand) {
        foldedStages = [...foldedStages, ...this.stageIds];
      }

      modelCustomSettings.set({ foldedStages });
    },
    async resortStages() {
      const res = await this.$refs.stageSortChangePopup.open();

      if (!res) return;

      stageModel.resort(res);
    },
    onTasksSelected(stage, ids) {
      this.selectedTasksByStage = { ...this.selectedTasksByStage, [stage.id]: ids };
    },
    resetCheck() {
      console.log('this.$refs.stages', this.$refs.stages);
      this.$refs.stages.forEach(st => st.resetCheck());
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.resetCheck();
      },
    },
    project(val) {
      if (!val) return;
      if (this.access.isProjectStandardMember()) {
        this.filters.hideEmptyStages = true;
      }
    },
    stageToScroll(val) {
      this.$nextTick(() => {
        const i = this.stages.findIndex(s => s?.id === val);
        if (i !== -1) {
          this.selectStage(i);
        }
      });
    },
  },
  mounted() {
    document.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    document.removeEventListener('scroll', this.onScroll);
  },
};
</script>

<style scoped lang="scss">
.stages {
  padding-top: 35px;
  display: grid;
  grid-template-columns: 5fr 2fr;
  grid-column-gap: 30px;

  &__block {
    margin-bottom: 30px;
  }

  &__filters {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;

    & > :first-child {
      width: 100%;
    }
  }


  &__title {
    font-size: 18px;
    line-height: 24px;
  }

  &__subtitle {
    font-weight: bold;
    font-size: 11px;
    line-height: 24px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-middle-gray);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
  }

  &__empty-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
  }

  &__aside {
    align-self: start;
  }

  &__aside-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    width: 100%;
    border-bottom: 1px solid var(--color-super-light);
    padding-bottom: 24px;
    margin-bottom: 24px;

    & > :first-child {
      margin-right: auto;
    }
  }

  &__member {
    padding: 6px 14px;
    background: var(--main-bg);
    margin-bottom: 10px;
  }

  @media (max-width: 1249px) {
    grid-template-columns: 1fr;

    &__aside {
      display: none;
    }
  }
}

</style>
